import React, { useState } from 'react';
import { X, Calendar, Plus } from 'lucide-react';

const HolidayCalendar = ({ holidays = [], onChange }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const addHoliday = () => {
    if (!startDate) return;
    // If no end date, add single date. Otherwise add range.
    const entry = endDate && endDate > startDate
      ? { start: startDate, end: endDate, label: `${formatDate(startDate)} - ${formatDate(endDate)}` }
      : { start: startDate, end: startDate, label: formatDate(startDate) };
    
    // Avoid exact duplicates
    const isDup = holidays.some(h => 
      (typeof h === 'string' ? h : h.start) === entry.start && 
      (typeof h === 'string' ? h : h.end) === entry.end
    );
    if (!isDup) {
      onChange([...holidays, entry]);
    }
    setStartDate('');
    setEndDate('');
  };

  const removeHoliday = (idx) => {
    onChange(holidays.filter((_, i) => i !== idx));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short', month: 'short', day: 'numeric'
    });
  };

  const getLabel = (h) => {
    if (typeof h === 'string') return formatDate(h);
    return h.label || `${formatDate(h.start)}${h.end && h.end !== h.start ? ` - ${formatDate(h.end)}` : ''}`;
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              data-testid="holiday-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={today}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To (optional)</label>
            <input
              type="date"
              data-testid="holiday-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || today}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50"
            />
          </div>
        </div>
        <button
          type="button"
          data-testid="holiday-add-btn"
          onClick={addHoliday}
          disabled={!startDate}
          className="w-full py-2 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 flex items-center justify-center gap-1"
        >
          <Plus size={14} /> Add Holiday
        </button>
      </div>

      {holidays.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-gray-500">Holidays ({holidays.length})</p>
          {holidays.map((h, idx) => (
            <div key={h.id || h.name || `${h.title || "h"}-${idx}`}
              className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-red-400" />
                <span className="text-xs text-gray-700">{getLabel(h)}</span>
              </div>
              <button type="button" onClick={() => removeHoliday(idx)}
                className="text-red-400 hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HolidayCalendar;
